import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
    getAccountingProviders,
    createAccountingProvider,
    updateAccountingProvider,
    deleteAccountingProvider
} from '../../api/common';

const AUTH_TYPES = ['basic', 'bearer', 'apiKey'];

const AUTH_TYPE_LABELS = { basic: 'Basic', bearer: 'Bearer Token', apiKey: 'API Key' };

const BADGE_CLASS = { basic: 'bg-secondary', bearer: 'bg-primary', apiKey: 'bg-info' };

function buildCredentialFields(authType, editing = false) {
    if (authType === 'basic') {
        return `
            <div class="mb-2">
                <input id="swal-cred-username" class="swal2-input" placeholder="Usuario" required />
            </div>
            <div class="mb-2">
                <input id="swal-cred-password" type="password" class="swal2-input"
                    placeholder="${editing ? '••••••  (dejar vacío para no cambiar)' : 'Contraseña'}"
                    ${editing ? '' : 'required'} />
            </div>`;
    }
    if (authType === 'bearer') {
        return `<div class="mb-2">
            <input id="swal-cred-token" type="password" class="swal2-input"
                placeholder="${editing ? '••••••  (dejar vacío para no cambiar)' : 'Token'}"
                ${editing ? '' : 'required'} />
        </div>`;
    }
    if (authType === 'apiKey') {
        return `
            <div class="mb-2">
                <input id="swal-cred-header" class="swal2-input" placeholder="Nombre del header (ej: X-API-Key)" required />
            </div>
            <div class="mb-2">
                <input id="swal-cred-value" type="password" class="swal2-input"
                    placeholder="${editing ? '••••••  (dejar vacío para no cambiar)' : 'Valor'}"
                    ${editing ? '' : 'required'} />
            </div>`;
    }
    return '';
}

function buildModalHtml(initialAuthType = 'basic', provider = null) {
    const editing = provider !== null;
    return `
        <div class="text-start">
            <div class="mb-2">
                <label class="form-label fw-semibold">Nombre visible</label>
                <input id="swal-display-name" class="swal2-input" placeholder="Ej: Alegra"
                    value="${editing ? (provider.display_name || '') : ''}" required />
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Identificador interno</label>
                <input id="swal-name" class="swal2-input" placeholder="ej: alegra (minúsculas, sin espacios)"
                    value="${editing ? (provider.name || '') : ''}"
                    ${editing ? 'readonly' : ''} required />
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">URL Base</label>
                <input id="swal-base-url" class="swal2-input" placeholder="https://api.proveedor.com/v1"
                    value="${editing ? (provider.base_url || '') : ''}" required />
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Tipo de autenticación</label>
                <select id="swal-auth-type" class="swal2-input">
                    ${AUTH_TYPES.map(t => `<option value="${t}" ${(editing ? provider.auth_type : initialAuthType) === t ? 'selected' : ''}>${AUTH_TYPE_LABELS[t]}</option>`).join('')}
                </select>
            </div>
            <div id="swal-cred-container">
                ${buildCredentialFields(editing ? provider.auth_type : initialAuthType, editing)}
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Headers por defecto (JSON, opcional)</label>
                <textarea id="swal-default-headers" class="swal2-textarea" placeholder='{"Content-Type":"application/json"}'>${editing && provider.default_headers ? JSON.stringify(provider.default_headers, null, 2) : ''}</textarea>
            </div>
        </div>`;
}

function readCredentials(authType, editing = false) {
    if (authType === 'basic') {
        const username = document.getElementById('swal-cred-username')?.value?.trim();
        const password = document.getElementById('swal-cred-password')?.value;
        const creds = {};
        if (username) creds.username = username;
        if (password) creds.password = password;
        if (!editing && (!username || !password)) return { error: 'Usuario y contraseña son requeridos' };
        return { creds };
    }
    if (authType === 'bearer') {
        const token = document.getElementById('swal-cred-token')?.value;
        if (!editing && !token) return { error: 'El token es requerido' };
        return { creds: token ? { token } : {} };
    }
    if (authType === 'apiKey') {
        const header = document.getElementById('swal-cred-header')?.value?.trim();
        const value = document.getElementById('swal-cred-value')?.value;
        const creds = {};
        if (header) creds.header = header;
        if (value) creds.value = value;
        if (!editing && (!header || !value)) return { error: 'Header y valor son requeridos' };
        return { creds };
    }
    return { creds: {} };
}

export default function ProveedoresTab() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAccountingProviders();
            if (res?.data?.success) setProviders(res.data.data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function openModal(provider = null) {
        const editing = provider !== null;
        const { value: confirmed } = await Swal.fire({
            title: editing ? 'Editar Proveedor' : 'Nuevo Proveedor',
            html: buildModalHtml('basic', provider),
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            width: 600,
            didOpen: () => {
                const authSelect = document.getElementById('swal-auth-type');
                if (authSelect) {
                    authSelect.addEventListener('change', () => {
                        const container = document.getElementById('swal-cred-container');
                        if (container) container.innerHTML = buildCredentialFields(authSelect.value, editing);
                    });
                }
            },
            preConfirm: () => {
                const display_name = document.getElementById('swal-display-name')?.value?.trim();
                const name = document.getElementById('swal-name')?.value?.trim();
                const base_url = document.getElementById('swal-base_url') 
                    ? document.getElementById('swal-base_url').value.trim()
                    : document.getElementById('swal-base-url')?.value?.trim();
                const auth_type = document.getElementById('swal-auth-type')?.value;
                const headersRaw = document.getElementById('swal-default-headers')?.value?.trim();

                if (!display_name || (!editing && !name) || !base_url || !auth_type) {
                    Swal.showValidationMessage('Completa todos los campos requeridos');
                    return false;
                }
                if (!/^https?:\/\/.+/.test(base_url)) {
                    Swal.showValidationMessage('URL base debe iniciar con http:// o https://');
                    return false;
                }
                if (!editing && !/^[a-z0-9_]+$/.test(name)) {
                    Swal.showValidationMessage('El identificador solo puede contener letras minúsculas, números y guion bajo');
                    return false;
                }

                const credResult = readCredentials(auth_type, editing);
                if (credResult.error) {
                    Swal.showValidationMessage(credResult.error);
                    return false;
                }

                let default_headers = {};
                if (headersRaw) {
                    try { default_headers = JSON.parse(headersRaw); }
                    catch { Swal.showValidationMessage('Headers por defecto no es JSON válido'); return false; }
                }

                const payload = { display_name, base_url, auth_type, default_headers };
                if (!editing) payload.name = name;
                if (Object.keys(credResult.creds).length) payload.auth_credentials = credResult.creds;
                return payload;
            }
        });

        if (!confirmed) return;

        try {
            let res;
            if (editing) {
                res = await updateAccountingProvider(provider._id, confirmed);
            } else {
                res = await createAccountingProvider(confirmed);
            }
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: editing ? 'Proveedor actualizado' : `Proveedor '${confirmed.display_name}' creado`, timer: 1800, showConfirmButton: false });
                load();
            } else {
                const msg = res?.data?.error || 'Error al guardar';
                Swal.fire({ icon: 'error', title: 'Error', text: msg });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    async function handleDeactivate(provider) {
        const { isConfirmed } = await Swal.fire({
            title: `¿Desactivar ${provider.display_name}?`,
            text: 'El proveedor dejará de aparecer en las integraciones activas.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, desactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (!isConfirmed) return;

        try {
            const res = await deleteAccountingProvider(provider._id);
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: 'Proveedor desactivado', timer: 1500, showConfirmButton: false });
                load();
            } else {
                const msg = res?.data?.error || 'No se pudo desactivar el proveedor';
                Swal.fire({ icon: 'error', title: 'No se puede desactivar', text: msg });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Proveedores Contables</h6>
                <button className="btn btn-primary btn-sm" onClick={() => openModal()}>+ Agregar Proveedor</button>
            </div>

            {loading ? (
                <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Nombre</th>
                                <th>Identificador</th>
                                <th>URL Base</th>
                                <th>Auth</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted py-3">No hay proveedores configurados</td></tr>
                            )}
                            {providers.map(p => (
                                <tr key={p._id}>
                                    <td>{p.display_name}</td>
                                    <td><code>{p.name}</code></td>
                                    <td className="text-truncate" style={{ maxWidth: 200 }}>{p.base_url}</td>
                                    <td><span className={`badge ${BADGE_CLASS[p.auth_type] || 'bg-secondary'}`}>{p.auth_type}</span></td>
                                    <td>
                                        {p.active
                                            ? <span className="badge bg-success">Activo</span>
                                            : <span className="badge bg-secondary">Inactivo</span>}
                                    </td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal(p)}>Editar</button>
                                        {p.active && (
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeactivate(p)}>Desactivar</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
